import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { take } from 'rxjs/operators';
import { ChartService } from '@app/data/services/chartService';
import { BasicChartDataSource } from '@app/data/interface/widget-config';
import moment from 'moment-timezone';
import { Site, SitesService } from '@app/data/services/sitesService';
import { FilterService, FilterSetup } from '@app/data/services/filterService';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import { Granularity } from '@app/data/services/types/EnvironmentalData';
import { EnvironmentalDataSourcesService } from '@app/data/services/environmentalDataSourceServices';
import { BaseExpandableWidgetComponent } from './base-expandable-widget-component';

@Directive()
export abstract class BaseFilteredEnvironmentalDataChartComponent
  extends BaseExpandableWidgetComponent
  implements OnInit
{
  @Input() startDateOverride?: string;
  @Input() endDateOverride?: string;
  @Input() targetSiteName: string | null = null;

  private _hasData: boolean = false;
  get hasData(): boolean {
    return this._hasData;
  }
  set hasData(value: boolean) {
    if (this._hasData === value) {
      return;
    }

    this._hasData = value;
    this.onHasDataChanged(value);
  }
  id: string = 'basic-cartesian-chart-x';
  chartTitle: string = '';
  chartSubtitle: string = '';
  domain: string = '';
  startDate: string = '';
  endDate: string = '';
  currentGranularity: Granularity = 'day';
  selectedEnvironmentalDataSources: string[] = [];
  selectedEnvironmentalDataSourceDashGroups: string[] = [];
  allEnvironmentalDataSources: EnvironmentalDataSource[] | null = null;
  currentSite: Site | null = null;
  protected lastAppliedEnvironmentalDataSourceIdentifiers: string[] = [];

  constructor(
    protected chartService: ChartService,
    protected filterService: FilterService,
    protected sitesService: SitesService,
    protected environmentalDataSourcesService: EnvironmentalDataSourcesService,
    elementRef: ElementRef<HTMLElement>,
    renderer: Renderer2
  ) {
    super(elementRef, renderer);
  }

  /**
   * Surface changes in the hasData flag to the expandable base class.
   * @param hasData The new hasData value.
   */
  protected onHasDataChanged(hasData: boolean): void {
    this.onWidgetDataAvailabilityChanged(hasData);
  }

  ngOnInit() {
    // Apply Filtering
    const filters = this.filterService.getFilters();
    this.startDate = this.startDateOverride ?? moment(filters.fromDate).format('YYYY-MM-DD');
    this.endDate = this.endDateOverride ?? moment(filters.toDate).format('YYYY-MM-DD');
    this.selectedEnvironmentalDataSources = this.sanitiseSelection(filters.selectedEnvironmentalDataSources);
    this.selectedEnvironmentalDataSourceDashGroups = this.sanitiseSelection(
      filters.selectedEnvironmentalDataSourceDashGroups
    );
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService.subscribe(this.handleSiteUpdate);
    this.environmentalDataSourcesService.subscribe(this.handleEnvironmentalDataSourcesUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.currentSite = site;
        this.setData();
      });
  }

  /**
   * Calculates the appropriate granularity based on the date range.
   *
   * @param startDate - The start date value.
   * @param endDate - The end date value.
   * @returns The granularity
   */
  protected calculateGranularity(startDate: moment.MomentInput, endDate: moment.MomentInput): Granularity {
    const dateRangeDays = moment(endDate).diff(moment(startDate), 'days');
    let granularity: Granularity = 'hour';

    granularity = this.getBaseGranularity(dateRangeDays, granularity);
    return granularity;
  }

  /**
   * Handles updates to the site parameters.
   *
   * Will reload the data if the site changes in the UI, so the data is correct
   * for the currently selected site.
   *
   * @param param0 The site update parameters.
   */
  protected handleSiteUpdate = ({ site }: any) => {
    if (site) {
      this.currentSite = site;
      this.setData();
    }
  };

  /**
   * Sanitises a selection array by removing invalid entries and handling 'all' logic.
   *
   * @param selection The input selection array which may contain invalid entries or 'all'.
   * @returns A sanitised array with unique valid entries, excluding 'all' if other entries exist.
   */
  private sanitiseSelection(selection: string[] | null | undefined): string[] {
    const entries = Array.isArray(selection) ? selection.filter((value): value is string => Boolean(value)) : [];
    if (entries.length === 0) return [];
    const uniqueEntries = Array.from(new Set(entries));
    if (uniqueEntries.length > 1 && uniqueEntries.includes('all')) {
      return uniqueEntries.filter((value) => value !== 'all');
    }
    return uniqueEntries;
  }

  protected handleEnvironmentalDataSourcesUpdate = (environmentalDataSources: EnvironmentalDataSource[]) => {
    this.allEnvironmentalDataSources = environmentalDataSources;
    this.setData();
  };

  protected siteCheck(): boolean {
    return !!this.currentSite && (this.targetSiteName === null || this.currentSite.name === this.targetSiteName);
  }

  // All should extend this.
  protected abstract setWidgetData(): void;

  /**
   * Called by the base class when filters/change events will cause a data reload.
   * Implementations should clear any existing chart state (e.g. `chartOptions`, flags)
   * so the UI does not show stale data while new API calls are in flight.
   */
  protected abstract clearChartData(): void;

  // Global checks first
  protected setData(): void {
    if (!this.siteCheck()) return;
    if (!this.startDate || !this.endDate) return;
    if (this.endDate < this.startDate) return;

    // Set the data for the chart
    this.setWidgetData();
  }

  /**
   * Handles updates to the filter parameters.
   *
   * Will reload the data if the start and end dates change in the UI or when overriden by
   * the component props.
   *
   * Will force-clear the existing data if the filters change to avoid inconsistencies.
   *
   * @param param0 The filter update parameters.
   * @returns
   */
  protected handleFilterUpdate = ({ filters }: { filters: FilterSetup }) => {
    // Deal with this more logically
    let changed = false;

    // Start date
    if (filters.fromDate && !this.startDateOverride) {
      const newStartDate = moment(filters.fromDate).format('YYYY-MM-DD');
      if (newStartDate !== this.startDate) {
        this.startDate = newStartDate;
        changed = true;
      }
    }

    // End date
    if (filters.toDate && !this.endDateOverride) {
      const newEndDate = moment(filters.toDate).format('YYYY-MM-DD');
      if (newEndDate !== this.endDate) {
        this.endDate = newEndDate;
        changed = true;
      }
    }

    // Selected sources
    const newSelectedSources = this.sanitiseSelection(filters.selectedEnvironmentalDataSources);
    if (JSON.stringify(newSelectedSources) !== JSON.stringify(this.selectedEnvironmentalDataSources)) {
      this.selectedEnvironmentalDataSources = newSelectedSources;
      changed = true;
    }

    // Selected groups
    const newSelectedGroups = this.sanitiseSelection(filters.selectedEnvironmentalDataSourceDashGroups);
    if (JSON.stringify(newSelectedGroups) !== JSON.stringify(this.selectedEnvironmentalDataSourceDashGroups)) {
      this.selectedEnvironmentalDataSourceDashGroups = newSelectedGroups;
      changed = true;
    }

    if (changed) {
      this.clearChartData();
      this.setData();
    }
  };

  /**
   * Selects the relevant data sources based on the current filter settings.
   * Handles 'all' selections and explicit selections for sources and groups.
   * For cases where the end user selects specific sources or groups,
   * these are queried individually. If 'all' is selected without explicit selections,
   * all available sources are included.
   *
   * @param sources The available data sources to filter from.
   * @returns The filtered data sources that match the current selection.
   */
  protected selectDataSources(
    sources: BasicChartDataSource[],
    options?: {
      preserveConfiguredLabels?: boolean;
    }
  ) {
    const preserveConfiguredLabels = options?.preserveConfiguredLabels ?? false;
    this.lastAppliedEnvironmentalDataSourceIdentifiers = [];
    const rawSelectedSourceIds = (this.selectedEnvironmentalDataSources ?? []).filter((id) => !!id);
    const rawSelectedGroupIds = (this.selectedEnvironmentalDataSourceDashGroups ?? []).filter((id) => !!id);

    const explicitSelectedSourceIds = rawSelectedSourceIds.filter((id) => id !== 'all');
    const explicitSelectedGroupIds = rawSelectedGroupIds.filter((id) => id !== 'all');

    const hasAllSelectedSource = rawSelectedSourceIds.includes('all');
    const hasAllSelectedGroup = rawSelectedGroupIds.includes('all');
    const hasExplicitSourceSelection = explicitSelectedSourceIds.length > 0;
    const hasExplicitGroupSelection = explicitSelectedGroupIds.length > 0;

    console.debug('[BaseFilteredEnvironmentalDataChartComponent] selectDataSources invoked', {
      configSourceCount: sources.length,
      configSourceIdentifiers: sources.map((source) => source.environmentalDataSourceIdentifier),
      selectedSources: rawSelectedSourceIds,
      selectedGroups: rawSelectedGroupIds,
      explicitSelectedSources: explicitSelectedSourceIds,
      explicitSelectedGroups: explicitSelectedGroupIds,
      allEnvironmentalDataSourcesCount: this.allEnvironmentalDataSources?.length ?? 0,
    });

    // If we don't have any selections at all, or we don't have an all, well that's a problem.
    if (
      ((this.selectedEnvironmentalDataSourceDashGroups?.length ?? 0) === 0 &&
        (this.selectedEnvironmentalDataSources?.length ?? 0) === 0) ||
      (this.allEnvironmentalDataSources?.length ?? 0) === 0 ||
      sources.length === 0
    ) {
      console.debug('[BaseFilteredEnvironmentalDataChartComponent] selectDataSources returning empty', {
        hasSelectedGroups: (this.selectedEnvironmentalDataSourceDashGroups?.length ?? 0) > 0,
        hasSelectedSources: (this.selectedEnvironmentalDataSources?.length ?? 0) > 0,
        allEnvironmentalDataSourcesCount: this.allEnvironmentalDataSources?.length ?? 0,
        configSourceCount: sources.length,
      });
      return [];
    }

    const filteredEnvironmentalDataSourceMap = new Map<string, EnvironmentalDataSource>();
    const addEnvironmentalDataSource = (dataSource: EnvironmentalDataSource) => {
      if (!dataSource || !dataSource.source_identifier) return;
      if (!filteredEnvironmentalDataSourceMap.has(dataSource.source_identifier)) {
        filteredEnvironmentalDataSourceMap.set(dataSource.source_identifier, dataSource);
      }
    };

    // First of all filter by selected Ids if applicable.
    if (hasExplicitSourceSelection) {
      (this.allEnvironmentalDataSources ?? [])
        .filter((source) => source.id && explicitSelectedSourceIds.includes(source.id))
        .forEach((source) => addEnvironmentalDataSource(source));
    } else if (hasAllSelectedSource && !hasExplicitGroupSelection) {
      (this.allEnvironmentalDataSources ?? []).forEach((source) => addEnvironmentalDataSource(source));
    }

    // Now get the ones in the right group
    if (hasExplicitGroupSelection) {
      (this.allEnvironmentalDataSources ?? [])
        .filter((source) => source.dash_groupings?.some((group) => explicitSelectedGroupIds.includes(group)))
        .forEach((source) => addEnvironmentalDataSource(source));
    } else if (hasAllSelectedGroup && !hasExplicitSourceSelection) {
      (this.allEnvironmentalDataSources ?? []).forEach((source) => addEnvironmentalDataSource(source));
    }

    // Now get just the ids.
    const filteredEnvironmentalDataSources = Array.from(filteredEnvironmentalDataSourceMap.values());
    const filteredEnvironmentalDataSourceIds = filteredEnvironmentalDataSources.map(
      (source) => source.source_identifier
    );

    this.lastAppliedEnvironmentalDataSourceIdentifiers = [...filteredEnvironmentalDataSourceIds];

    const aggregateConfigSources = sources.filter((source) => source.environmentalDataSourceIdentifier === 'all');
    const nonAggregateConfigSources = sources.filter((source) => source.environmentalDataSourceIdentifier !== 'all');

    const usingPureAggregateSelection =
      aggregateConfigSources.length > 0 &&
      !hasExplicitSourceSelection &&
      !hasExplicitGroupSelection &&
      hasAllSelectedSource;

    const shouldIncludeAggregateSource = usingPureAggregateSelection || filteredEnvironmentalDataSourceIds.length === 0;
    const shouldExpandAggregateSource =
      aggregateConfigSources.length > 0 &&
      filteredEnvironmentalDataSourceIds.length > 0 &&
      !usingPureAggregateSelection;

    // Now filter the graph config sources by these ids.
    const applicableEnvironmentalDataSources: BasicChartDataSource[] = nonAggregateConfigSources
      .filter((source) => filteredEnvironmentalDataSourceIds.includes(source.environmentalDataSourceIdentifier))
      .map((source) => {
        const matchingSource = filteredEnvironmentalDataSources.find(
          (envSource) => envSource.source_identifier === source.environmentalDataSourceIdentifier
        );
        const resolvedLabel = preserveConfiguredLabels
          ? source.label
          : matchingSource?.human_readable_name ?? source.label;
        if (resolvedLabel === source.label) {
          return source;
        }
        return {
          ...source,
          label: resolvedLabel,
        };
      });

    if (shouldExpandAggregateSource) {
      const expandedAggregateSources: BasicChartDataSource[] = [];
      aggregateConfigSources.forEach((aggregateSource) => {
        filteredEnvironmentalDataSourceIds.forEach((identifier) => {
          const matchingSource = filteredEnvironmentalDataSources.find(
            (source) => source.source_identifier === identifier
          );

          const resolvedLabel = preserveConfiguredLabels
            ? aggregateSource.label
            : matchingSource?.human_readable_name ?? aggregateSource.label;

          expandedAggregateSources.push({
            ...aggregateSource,
            environmentalDataSourceIdentifier: identifier,
            label: resolvedLabel,
          });
        });
      });

      applicableEnvironmentalDataSources.push(...expandedAggregateSources);
    } else if (shouldIncludeAggregateSource) {
      aggregateConfigSources.forEach((aggregateSource) =>
        applicableEnvironmentalDataSources.push({ ...aggregateSource })
      );
    }

    console.debug('[BaseFilteredEnvironmentalDataChartComponent] selectDataSources returning', {
      filteredIds: filteredEnvironmentalDataSourceIds,
      applicableCount: applicableEnvironmentalDataSources.length,
      includedAggregateSource: shouldIncludeAggregateSource,
      expandedAggregateSource: shouldExpandAggregateSource,
      returnedIdentifiers: applicableEnvironmentalDataSources.map((source) => source.environmentalDataSourceIdentifier),
    });

    return applicableEnvironmentalDataSources;
  }

  /**
   * Get the base granularity for the chart based on the date range and initial granularity.
   *
   * @param dateRangeDays The number of days in the date range.
   * @param granularity The initial granularity.
   * @returns The adjusted granularity.
   */
  protected getBaseGranularity(dateRangeDays: number, granularity: Granularity) {
    if (dateRangeDays >= 365) {
      granularity = 'month';
    } else if (dateRangeDays > 180) {
      granularity = 'week';
    } else if (dateRangeDays > 2) {
      granularity = 'day';
    }
    return granularity;
  }
}
